import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { UserContext } from '../../App';
import ApiService from '../../ApiService';
import { Campaign, CampaignCriteria, CampaignStatusCount } from '../../types/Campaign';
import Utils from '../../components/Utils';
import { CircularProgress, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import Grid from '@mui/material/Grid2';
import CampaignStatusFilter from './CampaignStatusFilter';
import { ClearOutlined, SearchOutlined } from '@mui/icons-material';
import CampaignManagerGrid from './CampaignManagerGrid';
import CampaignManagerDataGridExportCsvButton from './CampaignManagerDataGridExportCsvButton';
import CampaignMonthSelect from './CampaignMonthSelect';
import CampaignIncludePendingSwitch from './CampaignIncludePendingSwitch';
import { AdvertiserContext } from '../../AdvertiserContext';
import { useNavigate } from 'react-router-dom';
import Block from '../../components/Block';
import Column from '../../components/Column';
import Heading from '../../components/Heading';
import Row from '../../components/Row';
import theme from '../../theme';
import { CampaignManagerPageContext } from '../../hooks/useCampaignManagerPage';
import useCampaignManagerPage from '../../hooks/useCampaignManagerPage';
import AccessGuard from '../../components/AccessGuard';
import { Dealer } from '../../types/Dealer';
import { USER_GROUP_ADMIN, USER_GROUP_AGENCY } from '../../types/User';
import AdvertiserAutocomplete from '../../components/AdvertiserAutocomplete';
import AgencySelector from '../../components/AgencySelector';
import { GridPaginationModel } from '@mui/x-data-grid';
import usePageTitle from '../../hooks/usePageTitle';
import OemProgramCheckboxAutoComplete from '../../components/OemProgramCheckboxAutoComplete';
import { OemProgramCriteria } from '../../types/Criteria/OemProgramCriteria';
import { OemProgram } from '../../types/DspEntity';

const PAGE = 'campaignManager.';
const PAGE_SIZE = 10;

export default function CampaignManagerPage() {
    const navigate = useNavigate();

    const campaignManagerPage = useCampaignManagerPage();
    const { setCampaignGridColumnVisibility } = campaignManagerPage;

    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [campaignStatusCounts, setCampaignStatusCounts] = useState<CampaignStatusCount[]>([]);
    const { userContext } = useContext(UserContext);
    const { advertiserContext, setAdvertiserContext, setDealer } = useContext(AdvertiserContext);

    // build & render title
    usePageTitle({ segments: ['Campaign Manager'] });

    const [campaignCriteria, setCampaignCriteria] = useState<CampaignCriteria>(() => {
        let criteria = Utils.localStorage(PAGE + 'campaignCriteria', null);
        if (criteria) {
            let value = JSON.parse(criteria);
            value.startDate = new Date(value.startDate);
            value.endDate = new Date(value.endDate);
            return value;
        }
        return {
            startDate: Utils.getMonthStart(0),
            endDate: Utils.getMonthEnd(0),
            deliveryStatusId: '',
            includePendingCampaigns: false,
        };
    });

    useEffect(() => {
        const criteria = {
            ...campaignCriteria,
            startDate: campaignCriteria.startDate.getTime(),
            endDate: campaignCriteria.endDate.getTime(),
        };
        const value = JSON.stringify(criteria);
        localStorage.setItem(PAGE + 'campaignCriteria', value);
    }, [campaignCriteria]);

    const prevCampaignCriteria = useRef(campaignCriteria);
    useEffect(() => {
        campaignCriteria.dealerIds =
            advertiserContext?.dealer && advertiserContext.dealer.id ? [advertiserContext.dealer.id] : null;
        campaignCriteria.agencyId = advertiserContext?.agencyId ? advertiserContext.agencyId : undefined;
        campaignCriteria.pageSize = PAGE_SIZE;

        ApiService.getCampaigns(campaignCriteria).then((response) => {
            setCampaigns(response.data);
        });

        const ignoredKeys = ['page', 'deliveryStatusId', 'sort'];
        const ignoredChangesOnly =
            ignoredKeys.some((key) => prevCampaignCriteria.current[key] !== campaignCriteria[key]) &&
            Object.keys(prevCampaignCriteria.current).every(
                (key) => ignoredKeys.includes(key) || prevCampaignCriteria.current[key] === campaignCriteria[key]
            );

        if (!ignoredChangesOnly) {
            ApiService.getCampaignStatusCounts(campaignCriteria).then((response) => {
                const data = response.data;

                const roleStatusCounts = userContext.isAdmin()
                    ? data
                    : data.filter((item) => item.statusId !== 'PENDING_COGNITION');

                setCampaignStatusCounts(roleStatusCounts);
            });
        }
        prevCampaignCriteria.current = campaignCriteria;
        // Depend on the dealer *id* (the only thing this effect reads), not the dealer object
        // reference. AdvertiserAutocomplete re-emits onChange with a freshly fetched dealer object
        // (same id, new reference) on mount-sync, and the clear path produces a new `new Dealer()`
        // each time — depending on the reference would re-fire this fetch endlessly on first load.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [advertiserContext.dealer?.id, advertiserContext.agencyId, campaignCriteria]);

    const [searchText, setSearchText] = useState(campaignCriteria.searchText);
    const [searchTextLoading, setSearchTextLoading] = useState(false);

    const oemProgramCriteria = useMemo((): OemProgramCriteria => {
        return {
            agencyIds: campaignCriteria.agencyIds ?? [],
            advertiserIds: campaignCriteria.dealerIds ?? [],
        };
    }, [campaignCriteria]);

    // Stabilize the props handed to AdvertiserAutocomplete. Passing a fresh object/array/function
    // on every render makes the autocomplete's internal `criteria` memo (and the useLayoutEffect
    // keyed on it) re-run on every parent re-render. That effect calls back into onChange ->
    // setDealer, which re-fires the campaign fetch effect below and re-renders us, creating an
    // endless statusCounts/search loop when selecting an advertiser/agency on first page load.
    const advertiserCriteria = useMemo(
        () => ({ agencyIds: advertiserContext.agencyId ? [advertiserContext.agencyId] : [] }),
        [advertiserContext.agencyId]
    );

    const handleAdvertiserChange = useCallback(
        (advertiser: Dealer | null): void => {
            setDealer(advertiser === null ? new Dealer() : advertiser);
        },
        [setDealer]
    );

    const handleOemProgramOnChange = useCallback(
        (oemPrograms: OemProgram[]): void => {
            setCampaignCriteria({
                ...campaignCriteria,
                oemProgramIds: oemPrograms.map((oemProgram: OemProgram) => oemProgram.id as number),
                oemProgramNames: oemPrograms.map((oemProgram: OemProgram) => oemProgram.oemProgram),
            });
        },
        [campaignCriteria, setCampaignCriteria]
    );

    const debounce = (func: (...args: any[]) => any, delay: number) => {
        let timeoutId: any;
        return (...args: any[]) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                func(...args);
            }, delay);
        };
    };

    const debouncedChangeSearchCriteria = useRef(
        debounce((campaignCriteria, value) => {
            setSearchTextLoading(false);
            changeSearchCriteria({ ...campaignCriteria, searchText: value, deliveryStatusId: '' });
        }, 1000)
    ).current;

    useEffect(() => {
        if (searchText === campaignCriteria.searchText) return;
        setSearchTextLoading(true);
        debouncedChangeSearchCriteria(campaignCriteria, searchText);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText]);

    function changeSearchCriteria(campaignCriteria: CampaignCriteria) {
        setCampaignCriteria({ ...campaignCriteria });
    }

    function clearSearch() {
        setSearchText('');
        localStorage.setItem(PAGE + 'campaignSearch', '');
    }

    function onPageChange(model: GridPaginationModel) {
        // The controlled DataGrid resolves an undefined page to NaN and re-emits this handler on
        // every render. Normalize both sides to a valid integer; NaN === NaN is false, so without
        // this the guard never short-circuits and setCampaignCriteria loops the fetch effect.
        const nextPage = Number.isInteger(model.page) ? model.page : 0;
        const currentPage = Number.isInteger(campaignCriteria.page) ? (campaignCriteria.page as number) : 0;
        if (nextPage === currentPage) return;
        setCampaignCriteria({ ...campaignCriteria, page: nextPage });
    }

    function onSortModelChange([order]: any) {
        if (!order) order = { field: 'dealer', sort: 'asc' };
        let { field, sort } = order;
        if (field === 'dealer') field = 'dealerName';
        if (field === 'deliveryStatus') field = 'deliveryStatusId';
        if (field === 'agency') field = 'agencyName';
        if (field === 'createdByUser') field = 'createdByUserName';
        setCampaignCriteria({ ...campaignCriteria, sort: `${field} ${sort}` });
    }

    const getSearchCampaignTooltip = (): React.ReactNode => {
        let tooltip: string = 'Search by Campaign Name.';

        if (userContext.isAdmin()) {
            tooltip = 'Search by DSP, Campaign ID, Advertiser, or Campaign Name.';
        }

        if (userContext.isAgency()) {
            tooltip = 'Search by Advertiser or Campaign Name.';
        }

        return <>{tooltip}</>;
    };

    return (
        <CampaignManagerPageContext.Provider value={campaignManagerPage}>
            <Column gap={0} flex={1}>
                <Block>
                    <Column gap={1}>
                        <Row spacing={2} justifyContent="space-between" alignItems="center">
                            <Heading>Campaigns</Heading>

                            <Row justifyContent="flex-end">
                                <CampaignManagerDataGridExportCsvButton campaignCriteria={campaignCriteria} />
                            </Row>
                        </Row>
                    </Column>
                </Block>

                <Block py={0}>
                    <Row gap={2} justifyContent="space-between" alignItems="flex-start">
                        <Grid container spacing={2} sx={{ width: '100%' }}>
                            <AccessGuard accessGroup={USER_GROUP_ADMIN}>
                                <Grid size={{ xs: 12, md: 6, xl: 3 }}>
                                    <AgencySelector
                                        variant="outlined"
                                        label="Agency"
                                        agencySelected={(agencyId) => {
                                            setAdvertiserContext(null, agencyId);
                                        }}
                                        agencyId={advertiserContext.agencyId}
                                        sx={{ width: '100%' }}
                                    />
                                </Grid>
                            </AccessGuard>

                            <AccessGuard accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}>
                                <Grid size={{ xl: 3, md: 6, xs: 12 }}>
                                    <AdvertiserAutocomplete
                                        label="Advertiser"
                                        size="small"
                                        value={advertiserContext?.dealer?.id ? advertiserContext.dealer.id : null}
                                        criteria={advertiserCriteria}
                                        onChange={handleAdvertiserChange}
                                        showInactiveAdvertisersToggle={true}
                                    />
                                </Grid>
                            </AccessGuard>

                            <AccessGuard accessGroup={USER_GROUP_ADMIN}>
                                <Grid size={{ xs: 12, md: 6, xl: 3 }}>
                                    <OemProgramCheckboxAutoComplete
                                        multiple
                                        mass
                                        size="small"
                                        value={campaignCriteria.oemProgramIds ?? []}
                                        criteria={oemProgramCriteria}
                                        onChange={handleOemProgramOnChange}
                                    />
                                </Grid>
                            </AccessGuard>

                            <Grid size={{ xs: 12, md: 6, xl: 3 }}>
                                <CampaignMonthSelect
                                    campaignCriteria={campaignCriteria}
                                    changeSearchCriteria={(criteria: any) => changeSearchCriteria({ ...criteria })}
                                />
                            </Grid>

                            <AccessGuard accessGroup={USER_GROUP_ADMIN}>
                                <Grid size={{ xs: 12, md: 6, xl: 3 }}>
                                    <CampaignIncludePendingSwitch
                                        campaignCriteria={campaignCriteria}
                                        changeSearchCriteria={(criteria: any) => changeSearchCriteria({ ...criteria })}
                                    />
                                </Grid>
                            </AccessGuard>
                        </Grid>

                        <TextField
                            sx={{
                                height: 40,
                                width: 400,
                                '& .MuiInputBase-input': {
                                    backgroundColor: campaignCriteria.searchText ? '#F2F5FF' : '',
                                    color: theme.palette.text.secondary,
                                    fontSize: theme.typography.fontSize,
                                    height: '1.64375em',

                                    '&::placeholder': {
                                        color: theme.palette.text.primary,
                                    },
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: campaignCriteria.searchText
                                        ? `${theme.palette.primary.main} !important`
                                        : '',
                                },
                            }}
                            id="searchText"
                            value={searchText}
                            autoComplete="off"
                            size="small"
                            label="Search Campaigns"
                            onChange={(event) => {
                                setSearchText(event.target.value);
                            }}
                            slotProps={{
                                input: {
                                    startAdornment:
                                        campaignCriteria.searchText?.length > 0 ? (
                                            ''
                                        ) : (
                                            <InputAdornment position="start">
                                                <Tooltip title={getSearchCampaignTooltip()}>
                                                    <SearchOutlined fontSize="small" color="action" />
                                                </Tooltip>
                                            </InputAdornment>
                                        ),
                                    endAdornment: searchTextLoading ? (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => clearSearch()} edge="end">
                                                <CircularProgress color="inherit" size={20} />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : campaignCriteria.searchText?.length > 0 ? (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => clearSearch()} edge="end">
                                                <ClearOutlined fontSize="small" color="action" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : (
                                        ''
                                    ),
                                },
                            }}
                        />
                    </Row>
                </Block>

                <Block flex={1}>
                    <Column sx={{ height: '100%' }}>
                        <CampaignStatusFilter
                            campaignStatusCounts={campaignStatusCounts}
                            selectedStatus={campaignCriteria.deliveryStatusId}
                            onClick={(value: string) => {
                                changeSearchCriteria({
                                    ...campaignCriteria,
                                    deliveryStatusId: value,
                                });
                            }}
                        />

                        <CampaignManagerGrid
                            campaigns={campaigns}
                            totalCampaigns={
                                campaignStatusCounts.find((c) => c.statusId === campaignCriteria.deliveryStatusId)
                                    ?.count || 0
                            }
                            currentPage={
                                Number.isInteger(campaignCriteria.page) ? (campaignCriteria.page as number) : 0
                            }
                            campaignCriteria={campaignCriteria}
                            deliveryStatusId={campaignCriteria.deliveryStatusId}
                            pageSize={PAGE_SIZE}
                            onPageChange={onPageChange}
                            onSortModelChange={onSortModelChange}
                            onShowDetails={(campaign: Campaign) => {
                                navigate(`/campaign/${campaign.id}/overview`);
                            }}
                            onGridColumnVisibilityChange={(gridColumnVisibility) => {
                                setCampaignGridColumnVisibility(gridColumnVisibility);
                            }}
                        />
                    </Column>
                </Block>
            </Column>
        </CampaignManagerPageContext.Provider>
    );
}
